import { useEffect, useState } from 'react';
import { RenderIf } from 'react-rainbow-components';
import { showAppNotification } from '@rainbow-modules/app';
import Button from 'components/Button';
import Pencil from 'components/icons/pencil2';
import { useUpdateInvoiceInternalNote } from '../api/hooks';
import { Invoice } from '../types';
import {
    VoidReasonBanner,
    VoidReasonLabel,
    VoidReasonText,
} from '../styled';
import {
    InternalNoteInput,
    NoteCard,
    NoteEditActions,
    NoteEditButton,
    NoteHeader,
    NoteLabel,
    NoteText,
    NotesPane,
} from './styled';

const text = (value?: string | null) => String(value ?? '').trim();

interface InvoiceNotesProps {
    organizationId: string;
    invoice: Invoice;
    onUpdated: () => void;
}

const InvoiceNotes = ({
    organizationId,
    invoice,
    onUpdated,
}: InvoiceNotesProps) => {
    const { mutateAsync: updateInternalNote, isLoading } = useUpdateInvoiceInternalNote(
        organizationId,
    );
    const note = text(invoice.document.note ?? invoice.document.notes);
    const savedInternalNote = text(invoice.internalNote || invoice.document.internalNote);
    const voidReason = invoice.status === 'void' ? text(invoice.voidNote) : '';
    const [draft, setDraft] = useState(savedInternalNote);
    const [shownNote, setShownNote] = useState(savedInternalNote);
    const [isEditing, setEditing] = useState(false);

    useEffect(() => {
        setEditing(false);
        setDraft(savedInternalNote);
        setShownNote(savedInternalNote);
        // Reset only when a different invoice is opened. A refresh of this
        // invoice must not replace a note that was just saved.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice.id]);

    const startEdit = () => {
        setDraft(shownNote);
        setEditing(true);
    };

    const cancelEdit = () => {
        setDraft(shownNote);
        setEditing(false);
    };

    const saveEdit = async () => {
        const next = draft.trim();
        if (next === shownNote) {
            setEditing(false);
            return;
        }
        try {
            const updated = await updateInternalNote({
                invoiceId: invoice.id,
                body: { internalNote: next },
            });
            const updatedNote = text(
                updated.data?.internalNote
                ?? updated.data?.document?.internalNote
                ?? next,
            );
            setShownNote(updatedNote);
            setDraft(updatedNote);
            setEditing(false);
            showAppNotification({
                title: 'Internal note saved',
                description: 'Only your team can see this note.',
                icon: 'success',
                timeout: 3000,
            });
            onUpdated();
        } catch (error) {
            showAppNotification({
                title: 'Could not save internal note',
                description: (error as Error)?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
    };

    return (
        <NotesPane>
            <RenderIf isTrue={Boolean(voidReason)}>
                <VoidReasonBanner>
                    <VoidReasonLabel>Void reason</VoidReasonLabel>
                    <VoidReasonText>{voidReason}</VoidReasonText>
                </VoidReasonBanner>
            </RenderIf>
            <RenderIf isTrue={Boolean(note)}>
                <NoteCard>
                    <NoteLabel>Invoice note</NoteLabel>
                    <NoteText>{note}</NoteText>
                </NoteCard>
            </RenderIf>
            <NoteCard>
                <NoteHeader>
                    <NoteLabel>Internal note</NoteLabel>
                    <RenderIf isTrue={!isEditing}>
                        <NoteEditButton
                            type="button"
                            aria-label="Edit internal note"
                            title="Edit internal note"
                            onClick={startEdit}
                        >
                            <Pencil />
                        </NoteEditButton>
                    </RenderIf>
                </NoteHeader>
                <RenderIf isTrue={isEditing}>
                    <InternalNoteInput
                        hideLabel
                        placeholder="Only visible to your team, not shown on the invoice"
                        borderRadius="semi-square"
                        rows={4}
                        maxLength={2000}
                        value={draft}
                        disabled={isLoading}
                        onChange={(event) => setDraft(event.target.value)}
                    />
                    <NoteEditActions>
                        <Button
                            label="Cancel"
                            borderRadius="semi-rounded"
                            variant="outline-brand"
                            size="small"
                            disabled={isLoading}
                            onClick={cancelEdit}
                        />
                        <Button
                            label="Save"
                            borderRadius="semi-rounded"
                            variant="brand"
                            size="small"
                            isLoading={isLoading}
                            onClick={saveEdit}
                        />
                    </NoteEditActions>
                </RenderIf>
                <RenderIf isTrue={!isEditing && Boolean(shownNote)}>
                    <NoteText>{shownNote}</NoteText>
                </RenderIf>
            </NoteCard>
        </NotesPane>
    );
};

export default InvoiceNotes;
